import { createAsyncThunk } from "@reduxjs/toolkit";

import { patchSubscription } from "Api/organizations/subscription/patchSubscription.request";
import logger from "Libs/logger";
import { selectUserProjectById } from "Reducers/app/selectors";
import { getFeatureFlag } from "Reducers/featureFlags";
import { organizationsSelector } from "Reducers/organization/organization";
import {
  UpdateProjectType,
  UpdateProjectReturnPayload,
  gitProjectSelector
} from "Reducers/project/project";

import type { AsyncThunkOptionType } from "Reducers/types";

export const updateProject = createAsyncThunk<
  UpdateProjectReturnPayload,
  UpdateProjectType,
  AsyncThunkOptionType
>(
  "app/project_reducer/update",
  async (
    { organizationDescriptionId, projectDescriptionId, data, throughGit },
    { rejectWithValue, getState }
  ) => {
    try {
      const projectMe = selectUserProjectById(getState(), {
        projectId: projectDescriptionId
      });

      const project = gitProjectSelector(getState(), {
        organizationId: organizationDescriptionId,
        projectId: projectDescriptionId
      });

      if (!project) {
        return rejectWithValue("project does not exist");
      }

      const organizationEnabled = getFeatureFlag("ENABLE_ORGANIZATION");
      const organizations = organizationsSelector(getState());
      let projectResult = project;

      if (organizationEnabled) {
        const cleanData = {
          ...data,
          project_title: (data as { title: string }).title
        };

        // keeping this key/value pair will throw an error --> "Validation failed: title is not a known field."
        delete cleanData["title"];

        if (throughGit) {
          projectResult = (await project.update(data)).getEntity();
        } else {
          await patchSubscription({
            organizationId: project.organization_id,
            subscriptionId: project.getSubscriptionId(),
            data: cleanData
          });
          // patchSubscription doesn't return the updated project, so reflect the
          // changed fields locally to keep the store in sync with the save.
          projectResult = project.updateLocal({ ...project, ...data });
        }
      } else {
        projectResult = (await project.update(data)).getEntity();
      }

      return {
        project: projectResult,
        organizations,
        projectMe
      };
    } catch (err) {
      logger(err, {
        action: "project_update",
        organizationDescriptionId,
        projectDescriptionId,
        data
      });

      return rejectWithValue(err);
    }
  }
);
